import { generateClient } from 'aws-amplify/api'
import { whenAuthStorageReady } from '../config/amplify'
import { env } from '../config/env'
import { serializeAwsJson } from '../utils/aws-json'
import { formatUnknownError } from '../utils/format-error'
import { getDeviceId, rotateDeviceId } from '../utils/device-id'
import {
  getInstallSecretUserId,
  isDeviceVerificationError,
  isStillRegisteringError,
  readInstallSecret,
  readInstallSecretState,
  saveInstallSecret,
  serialiseDeviceCall,
} from './device-install-secret'

const client = generateClient()

export type AttachmentType = 'IMAGE' | 'GIF' | 'VIDEO' | 'FILE' | 'VOICE'
export type ConversationType = 'DIRECT' | 'GROUP' | 'ANNOUNCEMENT'
export type MessageType = 'TEXT' | 'MEDIA' | 'SYSTEM'

export type PresenceStatus = 'ACTIVE' | 'IDLE' | 'OFFLINE'

export type UserProfile = {
  userId: string
  email: string
  displayName: string
  givenName?: string | null
  familyName?: string | null
  roleType?: string | null
  providerLevel?: number | null
  avatarUrl?: string | null
  title?: string | null
  settingsJson?: string | null
  presenceStatus?: PresenceStatus | null
  lastActiveAt?: string | null
  /** listUsers only: false = you may not see this person's presence (status unknown, not offline). */
  presenceVisible?: boolean | null
  status?: 'ACTIVE' | 'INACTIVE' | 'PENDING_INVITE' | null
  archivedAt?: string | null
  /** True when Independent was removed from all Member associations (AHP). */
  associationRemovalNotice?: boolean | null
  /** Durable recording-policy decision (Members/Independents): ACCEPTED | DECLINED. */
  recordingPolicyConsentStatus?: string | null
  recordingPolicyConsentAt?: string | null
  recordingPolicyConsentNoticeVersion?: string | null
  createdAt?: string
  updatedAt?: string
}

/**
 * The caller's setup-related Users fields (`mySetupProfile`). Read with its own
 * query so the main `me` profile load never depends on them.
 */
export type SetupProfile = {
  userId: string
  roleType?: string | null
  providerLevel?: number | null
  recordingPolicyConsentStatus?: string | null
  recordingPolicyConsentAt?: string | null
  recordingPolicyConsentNoticeVersion?: string | null
  /** AWSJSON (`UserSetupState`). */
  setupState?: unknown
  /** AWSJSON (`SetupRequests`), stamped by admin actions. */
  setupRequests?: unknown
  /** AWSJSON (`SetupHealth`), server rollup of this user's device reports. */
  setupHealth?: unknown
}

export type UpdateMeInput = {
  displayName?: string
  givenName?: string
  familyName?: string
  avatarUrl?: string
  title?: string
  settingsJson?: Record<string, unknown>
  dismissAssociationRemovalNotice?: boolean
  /** Durable recording-policy decision: true = accept, false = decline (server-stamped). */
  recordingPolicyConsent?: boolean
  /** Where the decision was made (setup / resend / call / settings); selects the recorded wording. */
  recordingPolicyConsentContext?: 'setup' | 'resend' | 'call' | 'settings' | 'in-call'
}

export type AdminUpdateUserRoleInput = {
  userId: string
  roleType: string
  providerLevel?: number
}

export type AdminUpdateUserSettingsInput = {
  userId: string
  settingsPatch: Record<string, unknown>
}

export type Conversation = {
  conversationId: string
  type: ConversationType
  title?: string | null
  coverImageUrl?: string | null
  participantIds: string[]
  lastMessageAt?: string | null
  lastMessagePreview?: string | null
  lastMessageSenderId?: string | null
  createdAt: string
  createdBy: string
  lastReadAt?: string | null
  unread?: boolean | null
  readByOthersAt?: string | null
}

export type MessageReaction = {
  emoji: string
  userIds: string[]
}

export type Message = {
  messageId: string
  conversationId: string
  senderId: string
  body?: string | null
  messageType: MessageType
  attachmentUrl?: string | null
  attachmentType?: AttachmentType | null
  mentionIds?: string[] | null
  reactions?: MessageReaction[] | null
  editedAt?: string | null
  deletedAt?: string | null
  isDeleted?: boolean | null
  createdAt: string
}

export type SendMessageInput = {
  conversationId: string
  body?: string
  messageType?: MessageType
  attachmentUrl?: string
  attachmentType?: AttachmentType
  mentionIds?: string[]
}

export type CreateConversationInput = {
  type: ConversationType
  title?: string
  participantIds: string[]
}

export type UpdateConversationInput = {
  conversationId: string
  title?: string
  /** Set a custom group cover image (data URL); empty string clears it. */
  coverImageUrl?: string
  addParticipantIds?: string[]
  removeParticipantIds?: string[]
}

function assertAppSyncConfigured() {
  if (!env.appsyncGraphqlUrl) {
    throw new Error(
      'VITE_APPSYNC_GRAPHQL_URL is not set. Run infrastructure/scripts/deploy-chat.sh first.',
    )
  }
}

/**
 * Low-level entry point for GraphQL *subscriptions* (realtime hooks). Returns
 * the raw Amplify observable; callers cast it to their locally-typed
 * `{ subscribe(handlers) → { unsubscribe } }` shape. Lives here so that
 * `aws-amplify` never has to be imported outside the service layer.
 */
export function graphqlSubscription(
  query: string,
  variables?: Record<string, unknown>,
): unknown {
  return client.graphql({ query, variables, authMode: 'userPool' })
}

async function runGraphql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  // Native: wait for Preferences token storage + migration so the first inbox
  // fetch never races Amplify still reading an empty/default store ("No federated jwt").
  await whenAuthStorageReady()
  try {
    const result = await client.graphql({ query, variables })
    if ('errors' in result && result.errors?.length) {
      throw new Error(result.errors.map((e) => formatUnknownError(e)).join('; '))
    }
    if (!('data' in result) || result.data == null) {
      throw new Error('Empty GraphQL response')
    }
    return result.data as T
  } catch (err) {
    throw new Error(formatUnknownError(err))
  }
}

const meQuery = /* GraphQL */ `
  query Me {
    me {
      userId
      email
      displayName
      givenName
      familyName
      roleType
      providerLevel
      avatarUrl
      title
      settingsJson
      status
      associationRemovalNotice
      recordingPolicyConsentStatus
      recordingPolicyConsentAt
      recordingPolicyConsentNoticeVersion
      createdAt
    }
  }
`

const listUsersQuery = /* GraphQL */ `
  query ListUsers($limit: Int, $offset: Int) {
    listUsers(limit: $limit, offset: $offset) {
      userId
      email
      displayName
      givenName
      familyName
      roleType
      providerLevel
      avatarUrl
      title
      settingsJson
      presenceStatus
      lastActiveAt
      presenceVisible
      status
      archivedAt
    }
  }
`

const searchUsersQuery = /* GraphQL */ `
  query SearchUsers($search: String) {
    listUsers(search: $search) {
      userId
      email
      displayName
      givenName
      familyName
      roleType
      providerLevel
      avatarUrl
      title
      settingsJson
      presenceStatus
      lastActiveAt
      presenceVisible
      status
      archivedAt
    }
  }
`

const listDeactivatedUsersQuery = /* GraphQL */ `
  query ListDeactivatedUsers($userIds: [ID!]!) {
    listDeactivatedUsers(userIds: $userIds) {
      userId
      status
      displayName
    }
  }
`

const updatePresenceMutation = /* GraphQL */ `
  mutation UpdatePresence($input: UpdatePresenceInput!) {
    updatePresence(input: $input) {
      userId
      presenceStatus
      lastActiveAt
    }
  }
`

const updateMessageMutation = /* GraphQL */ `
  mutation UpdateMessage($input: UpdateMessageInput!) {
    updateMessage(input: $input) {
      messageId
      conversationId
      senderId
      body
      messageType
      attachmentUrl
      attachmentType
      mentionIds
      editedAt
      deletedAt
      isDeleted
      createdAt
    }
  }
`

const deleteMessageMutation = /* GraphQL */ `
  mutation DeleteMessage($conversationId: ID!, $messageId: ID!) {
    deleteMessage(conversationId: $conversationId, messageId: $messageId) {
      messageId
      conversationId
      senderId
      body
      messageType
      isDeleted
      deletedAt
      createdAt
    }
  }
`

const undeleteMessageMutation = /* GraphQL */ `
  mutation UndeleteMessage($conversationId: ID!, $messageId: ID!) {
    undeleteMessage(conversationId: $conversationId, messageId: $messageId) {
      messageId
      conversationId
      senderId
      body
      messageType
      isDeleted
      deletedAt
      createdAt
    }
  }
`

const getAttachmentUploadUrlQuery = /* GraphQL */ `
  query GetAttachmentUploadUrl($input: GetAttachmentUploadUrlInput!) {
    getAttachmentUploadUrl(input: $input) {
      uploadUrl
      attachmentUrl
      attachmentKey
      expiresAt
      expiresIn
    }
  }
`

const getAttachmentDownloadUrlQuery = /* GraphQL */ `
  query GetAttachmentDownloadUrl($attachmentUrl: String!) {
    getAttachmentDownloadUrl(attachmentUrl: $attachmentUrl) {
      downloadUrl
      expiresAt
    }
  }
`

const updateMeMutation = /* GraphQL */ `
  mutation UpdateMe($input: UpdateMeInput!) {
    updateMe(input: $input) {
      userId
      email
      displayName
      givenName
      familyName
      avatarUrl
      title
      roleType
      providerLevel
      settingsJson
      associationRemovalNotice
      recordingPolicyConsentStatus
      recordingPolicyConsentAt
      recordingPolicyConsentNoticeVersion
    }
  }
`

const deleteMyAccountMutation = /* GraphQL */ `
  mutation DeleteMyAccount {
    deleteMyAccount
  }
`

const adminUpdateUserRoleMutation = /* GraphQL */ `
  mutation AdminUpdateUserRole($input: AdminUpdateUserRoleInput!) {
    adminUpdateUserRole(input: $input) {
      userId
      email
      displayName
      roleType
      providerLevel
      settingsJson
      status
      archivedAt
    }
  }
`

const adminUpdateUserSettingsMutation = /* GraphQL */ `
  mutation AdminUpdateUserSettings($input: AdminUpdateUserSettingsInput!) {
    adminUpdateUserSettings(input: $input) {
      userId
      email
      displayName
      roleType
      providerLevel
      settingsJson
      status
      archivedAt
    }
  }
`

const notifyCoverageChangeMutation = /* GraphQL */ `
  mutation NotifyCoverageChange($input: NotifyCoverageChangeInput!) {
    notifyCoverageChange(input: $input) {
      notified
    }
  }
`

const adminDeactivateUserMutation = /* GraphQL */ `
  mutation AdminDeactivateUser($userId: ID!) {
    adminDeactivateUser(userId: $userId) {
      userId
      email
      displayName
      roleType
      providerLevel
      status
      archivedAt
    }
  }
`

const listConversationsQuery = /* GraphQL */ `
  query ListConversations($limit: Int) {
    listConversations(limit: $limit) {
      conversationId
      type
      title
      coverImageUrl
      participantIds
      lastMessageAt
      lastMessagePreview
      createdAt
      createdBy
      lastReadAt
      unread
      readByOthersAt
    }
  }
`

const listMessagesQuery = /* GraphQL */ `
  query ListMessages(
    $conversationId: ID!
    $limit: Int
    $nextToken: String
    $sortDirection: ModelSortDirection
  ) {
    listMessages(
      conversationId: $conversationId
      limit: $limit
      nextToken: $nextToken
      sortDirection: $sortDirection
    ) {
      items {
        messageId
        conversationId
        senderId
        body
        messageType
        attachmentUrl
        attachmentType
        mentionIds
        reactions {
          emoji
          userIds
        }
        editedAt
        deletedAt
        isDeleted
        createdAt
      }
      nextToken
    }
  }
`

export type MessagePage = {
  items: Message[]
  nextToken: string | null
}

const createConversationMutation = /* GraphQL */ `
  mutation CreateConversation($input: CreateConversationInput!) {
    createConversation(input: $input) {
      conversationId
      type
      title
      participantIds
      createdAt
      createdBy
    }
  }
`

const getConversationQuery = /* GraphQL */ `
  query GetConversation($conversationId: ID!) {
    getConversation(conversationId: $conversationId) {
      conversationId
      type
      title
      coverImageUrl
      participantIds
      lastMessageAt
      lastMessagePreview
      createdAt
      createdBy
    }
  }
`

const updateConversationMutation = /* GraphQL */ `
  mutation UpdateConversation($input: UpdateConversationInput!) {
    updateConversation(input: $input) {
      conversationId
      type
      title
      coverImageUrl
      participantIds
      lastMessageAt
      lastMessagePreview
      createdAt
      createdBy
    }
  }
`

const markConversationReadMutation = /* GraphQL */ `
  mutation MarkConversationRead($conversationId: ID!, $readAt: AWSDateTime!) {
    markConversationRead(conversationId: $conversationId, readAt: $readAt)
  }
`

const sendMessageMutation = /* GraphQL */ `
  mutation SendMessage($input: SendMessageInput!) {
    sendMessage(input: $input) {
      messageId
      conversationId
      senderId
      body
      messageType
      attachmentUrl
      attachmentType
      mentionIds
      reactions {
        emoji
        userIds
      }
      createdAt
    }
  }
`

const addReactionMutation = /* GraphQL */ `
  mutation AddReaction($input: AddReactionInput!) {
    addReaction(input: $input) {
      messageId
      conversationId
      reactions {
        emoji
        userIds
      }
    }
  }
`

const removeReactionMutation = /* GraphQL */ `
  mutation RemoveReaction($input: RemoveReactionInput!) {
    removeReaction(input: $input) {
      messageId
      conversationId
      reactions {
        emoji
        userIds
      }
    }
  }
`

/**
 * `me` is read at boot by the auth profile load AND by every screen's
 * `getCurrentChatUser()` (Messages, Calls, Notifications poll, on-call, car
 * sync …), usually within the same second. Callers inside this window (or while
 * the first read is in flight) share ONE request; profile mutations and
 * sign-out drop it so a read after a change is never stale.
 */
export const ME_COALESCE_MS = 3_000

type MeRead = { at: number; promise: Promise<UserProfile> }
let meRead: MeRead | null = null

export function fetchMe(): Promise<UserProfile> {
  if (meRead && Date.now() - meRead.at < ME_COALESCE_MS) return meRead.promise
  const entry: MeRead = {
    at: Date.now(),
    promise: (async () => {
      assertAppSyncConfigured()
      const data = await runGraphql<{ me: UserProfile }>(meQuery)
      return data.me
    })(),
  }
  meRead = entry
  entry.promise.catch(() => {
    if (meRead === entry) meRead = null
  })
  return entry.promise
}

/** Drop the coalesced `me` read (profile mutations, sign-out). */
export function invalidateMeCache(): void {
  meRead = null
}

// Kept out of the main `me` selection so an older backend without the field
// can never break the profile load the recording-policy state depends on.
const myConsentCallEndedNoticeQuery = /* GraphQL */ `
  query MyConsentCallEndedNotice {
    me {
      userId
      recordingConsentCallEndedNotice
    }
  }
`

const dismissConsentCallEndedNoticeMutation = /* GraphQL */ `
  mutation DismissConsentCallEndedNotice($input: UpdateMeInput!) {
    updateMe(input: $input) {
      userId
    }
  }
`

const mySetupProfileQuery = /* GraphQL */ `
  query MySetupProfile {
    me {
      userId
      roleType
      providerLevel
      recordingPolicyConsentStatus
      recordingPolicyConsentAt
      recordingPolicyConsentNoticeVersion
      setupState
      setupRequests
      setupHealth
    }
  }
`

const updateMySetupStateMutation = /* GraphQL */ `
  mutation UpdateMySetupState($input: UpdateMeInput!) {
    updateMe(input: $input) {
      userId
      setupState
    }
  }
`

/** The caller's setup fields (admin requests, cross-device setup state, consent). */
export async function fetchMySetupProfile(): Promise<SetupProfile | null> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ me: SetupProfile | null }>(mySetupProfileQuery)
  return data.me ?? null
}

/** Merge a whitelisted patch into the caller's cross-device setup state (server-merged). */
export async function updateMySetupState(patch: {
  tourVersionSeen?: number
  explainersSeen?: { sessionRecording?: string }
  lastCompletedFlowVersion?: number
}): Promise<{ userId: string; setupState?: unknown }> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ updateMe: { userId: string; setupState?: unknown } }>(
    updateMySetupStateMutation,
    { input: { setupState: serializeAwsJson(patch) } },
  )
  return data.updateMe
}

/** Raw AWSJSON of the pending "call ended — recording consent missing" notice, if any. */
export async function fetchMyConsentCallEndedNotice(): Promise<unknown> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ me: { recordingConsentCallEndedNotice?: unknown } | null }>(
    myConsentCallEndedNoticeQuery,
  )
  return data.me?.recordingConsentCallEndedNotice ?? null
}

export async function dismissMyConsentCallEndedNotice(callId: string): Promise<void> {
  assertAppSyncConfigured()
  await runGraphql(dismissConsentCallEndedNoticeMutation, {
    input: { dismissRecordingConsentCallEndedNotice: callId },
  })
}

export async function updateMeProfile(input: UpdateMeInput): Promise<UserProfile> {
  assertAppSyncConfigured()
  const { settingsJson, ...rest } = input
  const payload: Record<string, unknown> = { ...rest }
  if (settingsJson != null) {
    payload.settingsJson = serializeAwsJson(settingsJson)
  }
  invalidateMeCache()
  try {
    const data = await runGraphql<{ updateMe: UserProfile }>(updateMeMutation, { input: payload })
    return data.updateMe
  } finally {
    // Also after: a read that started while the mutation was in flight may
    // have captured the old profile.
    invalidateMeCache()
  }
}

export async function deleteMyAccount(): Promise<boolean> {
  invalidateMeCache()
  assertAppSyncConfigured()
  const data = await runGraphql<{ deleteMyAccount: boolean }>(deleteMyAccountMutation)
  return data.deleteMyAccount
}

export async function adminUpdateUserRole(
  input: AdminUpdateUserRoleInput,
): Promise<UserProfile> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ adminUpdateUserRole: UserProfile }>(
    adminUpdateUserRoleMutation,
    { input },
  )
  return data.adminUpdateUserRole
}

export async function adminUpdateUserSettings(
  input: AdminUpdateUserSettingsInput,
): Promise<UserProfile> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ adminUpdateUserSettings: UserProfile }>(
    adminUpdateUserSettingsMutation,
    {
      input: {
        userId: input.userId,
        settingsPatch: serializeAwsJson(input.settingsPatch),
      },
    },
  )
  return data.adminUpdateUserSettings
}

export type CoverageNotificationRecipient = {
  userId: string
  action: 'added' | 'removed'
  slotCount?: number
  slotLabels?: string[]
  /** "on-call" | "daytime" */
  domain?: string
}

/**
 * Notify providers about coverage-slot changes (added/removed). Writes Activity
 * feed rows + pushes FCM server-side. Notifying users other than yourself
 * requires owner / level-4 (enforced in the resolver). Returns the count of
 * recipients that were notified.
 */
export async function notifyCoverageChange(input: {
  recipients: CoverageNotificationRecipient[]
}): Promise<number> {
  assertAppSyncConfigured()
  if (input.recipients.length === 0) return 0
  const data = await runGraphql<{ notifyCoverageChange: { notified: number } }>(
    notifyCoverageChangeMutation,
    { input },
  )
  return data.notifyCoverageChange?.notified ?? 0
}

export async function adminDeactivateUser(userId: string): Promise<UserProfile> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ adminDeactivateUser: UserProfile }>(
    adminDeactivateUserMutation,
    { userId },
  )
  return data.adminDeactivateUser
}

export async function getAttachmentUploadUrl(input: {
  conversationId: string
  fileName: string
  contentType: string
  attachmentType: AttachmentType
}) {
  assertAppSyncConfigured()
  const data = await runGraphql<{
    getAttachmentUploadUrl: {
      uploadUrl: string
      attachmentUrl: string
      attachmentKey: string
    }
  }>(getAttachmentUploadUrlQuery, { input })
  return data.getAttachmentUploadUrl
}

export async function getAttachmentDownloadUrl(attachmentUrl: string) {
  assertAppSyncConfigured()
  const data = await runGraphql<{
    getAttachmentDownloadUrl: { downloadUrl: string; expiresAt?: string | null }
  }>(getAttachmentDownloadUrlQuery, { attachmentUrl })
  return data.getAttachmentDownloadUrl
}

/** One name-ordered page of the users the caller may see (`offset` 0-based; up to 1000 per page). */
export async function listUsers(limit = 50, offset?: number): Promise<UserProfile[]> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listUsers: UserProfile[] }>(listUsersQuery, { limit, offset })
  return data.listUsers ?? []
}

/** Exact lookup: active people whose whole email address or whole name matches `search` (max 20). */
export async function searchUsers(search: string): Promise<UserProfile[]> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listUsers: UserProfile[] }>(searchUsersQuery, { search })
  return data.listUsers ?? []
}

export type DeactivatedUserStub = {
  userId: string
  status: 'INACTIVE'
  displayName: string
}

/**
 * Which of these userIds are admin-deactivated. Returns redacted stubs (no
 * email / avatar / real name) so historical surfaces can relabel archived
 * people as "Deactivated user". Active / unknown ids are omitted.
 */
export async function listDeactivatedUsers(userIds: string[]): Promise<DeactivatedUserStub[]> {
  if (userIds.length === 0) return []
  assertAppSyncConfigured()
  const data = await runGraphql<{ listDeactivatedUsers: DeactivatedUserStub[] }>(
    listDeactivatedUsersQuery,
    { userIds },
  )
  return data.listDeactivatedUsers ?? []
}

/**
 * `listConversations(100)` is read by the inbox, the global unread badge, the
 * per-module rail counts, the notifications feed and the chime hook — each on
 * its own 30 s poll, all mounted together — which made it the busiest list
 * Lambda by ~3x (19k calls / 3 days on TEST). Callers arriving within this
 * window (or while the first is still in flight) share ONE request. Kept tiny
 * so a read right after the user acts is never visibly stale; every
 * conversation-changing mutation below also drops it.
 */
export const CONVERSATIONS_COALESCE_MS = 2_000

type ConversationsRead = { at: number; promise: Promise<Conversation[]> }
const conversationsReads = new Map<number, ConversationsRead>()

/** Forget coalesced reads (conversation mutations, sign-out, tests). */
export function invalidateConversationsListCache(): void {
  conversationsReads.clear()
}

/** Sign-out: forget every coalesced per-user read. */
export function resetCoalescedReads(): void {
  conversationsReads.clear()
  meRead = null
}

export function listConversations(limit = 30): Promise<Conversation[]> {
  const existing = conversationsReads.get(limit)
  if (existing && Date.now() - existing.at < CONVERSATIONS_COALESCE_MS) return existing.promise
  const entry: ConversationsRead = {
    at: Date.now(),
    promise: (async () => {
      assertAppSyncConfigured()
      const data = await runGraphql<{ listConversations: Conversation[] }>(listConversationsQuery, {
        limit,
      })
      return data.listConversations ?? []
    })(),
  }
  conversationsReads.set(limit, entry)
  // A failed read is never shared with later callers.
  entry.promise.catch(() => {
    if (conversationsReads.get(limit) === entry) conversationsReads.delete(limit)
  })
  return entry.promise
}

export async function listMessagesPage(
  conversationId: string,
  options: {
    limit?: number
    nextToken?: string | null
    sortDirection?: 'ASC' | 'DESC'
  } = {},
): Promise<MessagePage> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listMessages: MessagePage }>(listMessagesQuery, {
    conversationId,
    limit: options.limit ?? 50,
    nextToken: options.nextToken ?? undefined,
    sortDirection: options.sortDirection ?? 'ASC',
  })
  return {
    items: data.listMessages.items ?? [],
    nextToken: data.listMessages.nextToken ?? null,
  }
}

export async function listMessages(conversationId: string, limit = 50): Promise<Message[]> {
  const page = await listMessagesPage(conversationId, { limit, sortDirection: 'ASC' })
  return page.items
}

export async function createConversation(input: CreateConversationInput): Promise<Conversation> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ createConversation: Conversation }>(
    createConversationMutation,
    { input },
  )
  invalidateConversationsListCache()
  return data.createConversation
}

export async function getConversation(conversationId: string): Promise<Conversation> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ getConversation: Conversation }>(getConversationQuery, {
    conversationId,
  })
  return data.getConversation
}

export async function updateConversation(input: UpdateConversationInput): Promise<Conversation> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ updateConversation: Conversation }>(
    updateConversationMutation,
    { input },
  )
  invalidateConversationsListCache()
  return data.updateConversation
}

export async function sendMessage(input: SendMessageInput): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ sendMessage: Message }>(sendMessageMutation, { input })
  invalidateConversationsListCache()
  return data.sendMessage
}

export async function markConversationRead(
  conversationId: string,
  readAt: string,
): Promise<boolean> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ markConversationRead: boolean }>(
    markConversationReadMutation,
    { conversationId, readAt },
  )
  invalidateConversationsListCache()
  return data.markConversationRead
}

export async function addReaction(input: {
  conversationId: string
  messageId: string
  emoji: string
}): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ addReaction: Message }>(addReactionMutation, { input })
  return data.addReaction
}

export async function removeReaction(input: {
  conversationId: string
  messageId: string
  emoji: string
}): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ removeReaction: Message }>(removeReactionMutation, { input })
  return data.removeReaction
}

const publishTypingMutation = /* GraphQL */ `
  mutation PublishTyping($conversationId: ID!, $isTyping: Boolean!) {
    publishTyping(conversationId: $conversationId, isTyping: $isTyping) {
      conversationId
      userId
      displayName
      isTyping
    }
  }
`

export type TypingEvent = {
  conversationId: string
  userId: string
  displayName?: string | null
  isTyping: boolean
}

export async function publishTyping(
  conversationId: string,
  isTyping: boolean,
): Promise<void> {
  if (!env.appsyncGraphqlUrl) return
  try {
    await runGraphql<{ publishTyping: TypingEvent }>(publishTypingMutation, {
      conversationId,
      isTyping,
    })
  } catch {
    /* typing is ephemeral and best-effort — never surface errors */
  }
}

export async function updatePresence(presenceStatus: PresenceStatus): Promise<UserProfile> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ updatePresence: UserProfile }>(updatePresenceMutation, {
    input: { presenceStatus },
  })
  return data.updatePresence
}

const registerPushTokenMutation = /* GraphQL */ `
  mutation RegisterPushToken($input: RegisterPushTokenInput!) {
    registerPushToken(input: $input)
  }
`

export async function registerPushToken(
  token: string,
  platform: 'ANDROID' | 'IOS' | 'IOS_VOIP',
): Promise<void> {
  assertAppSyncConfigured()
  await runGraphql<{ registerPushToken: boolean }>(registerPushTokenMutation, {
    input: { token, platform },
  })
}

/**
 * Sign-out: remove the signed-in user's row for this device token so the
 * device stops receiving their calls and message previews. WEB passes its
 * subscription endpoint as the token.
 */
export async function unregisterPushToken(
  token: string,
  platform: 'ANDROID' | 'IOS' | 'IOS_VOIP' | 'WEB',
  forUserId: string,
): Promise<void> {
  assertAppSyncConfigured()
  await runGraphql<{ registerPushToken: boolean }>(registerPushTokenMutation, {
    input: { token, platform, unregister: true, unregisterForUserId: forUserId },
  })
}

export type WebPushSubscriptionPayload = {
  endpoint: string
  p256dh: string
  auth: string
}

/**
 * Register a browser Web Push subscription (platform WEB) so the backend can
 * deliver closed-tab notifications. The subscription's encryption keys travel in
 * `webSubscription`; there is no plain device `token` for web.
 */
export async function registerWebPushSubscription(
  subscription: WebPushSubscriptionPayload,
): Promise<void> {
  assertAppSyncConfigured()
  await runGraphql<{ registerPushToken: boolean }>(registerPushTokenMutation, {
    input: { platform: 'WEB', webSubscription: subscription },
  })
}

export async function updateMessage(input: {
  conversationId: string
  messageId: string
  body: string
}): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ updateMessage: Message }>(updateMessageMutation, { input })
  // The inbox preview / last-message fields may have changed.
  invalidateConversationsListCache()
  return data.updateMessage
}

export async function deleteMessage(
  conversationId: string,
  messageId: string,
): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ deleteMessage: Message }>(deleteMessageMutation, {
    conversationId,
    messageId,
  })
  // The inbox preview / last-message fields may have changed.
  invalidateConversationsListCache()
  return data.deleteMessage
}

export async function undeleteMessage(
  conversationId: string,
  messageId: string,
): Promise<Message> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ undeleteMessage: Message }>(undeleteMessageMutation, {
    conversationId,
    messageId,
  })
  // The inbox preview / last-message fields may have changed.
  invalidateConversationsListCache()
  return data.undeleteMessage
}

export type AnnouncementChannelEnum =
  | 'ALL'
  | 'STAFF_UPDATES'
  | 'UPDATES'
  | 'EVENTS'
  | 'JOBS'
  | 'PHOTO_OF_DAY'
  | 'CHITCHAT'
  | 'LEAVE_UPDATES'

export type AnnouncementUrgencyEnum = 'NORMAL' | 'IMPORTANT' | 'URGENT'

export type AnnouncementStatusEnum = 'APPROVED' | 'PENDING' | 'REJECTED'

export type ForumReviewDecision = 'APPROVE' | 'REJECT'

export type AnnouncementAudienceEnum =
  | 'EVERYONE'
  | 'MEMBERS'
  | 'PROVIDERS'
  | 'MEMBERS_AND_PROVIDERS'

export type AnnouncementPostGql = {
  postId: string
  channelId: AnnouncementChannelEnum
  title: string
  body: string
  authorId: string
  authorDisplayName: string
  urgency: AnnouncementUrgencyEnum
  audience: AnnouncementAudienceEnum
  attachmentUrl?: string | null
  attachmentType?: AttachmentType | null
  mentionIds?: string[] | null
  createdAt: string
  editedAt?: string | null
  commentCount: number
  readCount: number
  hasRead: boolean
  likeCount: number
  hasLiked: boolean
  /** JSON string map { optionId: count }; null when no poll / no votes yet. */
  pollVoteCounts?: string | null
  pollVoterCount?: number | null
  myPollVotes?: string[] | null
  /** Moderation status — null/APPROVED for legacy + published posts. */
  status?: AnnouncementStatusEnum | null
  /** Role type of the author at submission (member Forum review context). */
  authorRoleType?: string | null
  reviewedBy?: string | null
  reviewedByName?: string | null
  reviewedAt?: string | null
  rejectionReason?: string | null
}

export type AnnouncementCommentGql = {
  commentId: string
  postId: string
  authorId: string
  authorDisplayName: string
  body: string
  createdAt: string
  likeCount: number
  hasLiked: boolean
}

export type AnnouncementLikeTarget = 'POST' | 'COMMENT'

export type AnnouncementLikeResultGql = {
  targetType: AnnouncementLikeTarget
  targetId: string
  liked: boolean
  likeCount: number
}

export type AnnouncementPostConnection = {
  items: AnnouncementPostGql[]
  nextToken?: string | null
}

export type NotificationGql = {
  notificationId: string
  type: string
  actorId?: string | null
  actorName?: string | null
  targetType?: string | null
  postId?: string | null
  commentId?: string | null
  /** GraphQL announcement channel enum (e.g. UPDATES). */
  channelId?: string | null
  title?: string | null
  body?: string | null
  /** Prebuilt in-app route to the exact target. */
  path?: string | null
  createdAt: string
}

export type AnnouncementVoteResultGql = {
  postId: string
  myPollVotes: string[]
  /** JSON string map { optionId: count }. */
  pollVoteCounts: string
  pollVoterCount: number
}

const listAnnouncementPostsQuery = /* GraphQL */ `
  query ListAnnouncementPosts($channelId: AnnouncementChannel!, $limit: Int, $nextToken: String) {
    listAnnouncementPosts(channelId: $channelId, limit: $limit, nextToken: $nextToken) {
      items {
        postId
        channelId
        title
        body
        authorId
        authorDisplayName
        urgency
        audience
        attachmentUrl
        attachmentType
        mentionIds
        createdAt
        editedAt
        commentCount
        readCount
        hasRead
        likeCount
        hasLiked
        pollVoteCounts
        pollVoterCount
        myPollVotes
        status
        authorRoleType
        reviewedBy
        reviewedByName
        reviewedAt
        rejectionReason
      }
      nextToken
    }
  }
`

const listNotificationsQuery = /* GraphQL */ `
  query ListNotifications($limit: Int) {
    listNotifications(limit: $limit) {
      notificationId
      type
      actorId
      actorName
      targetType
      postId
      commentId
      channelId
      title
      body
      path
      createdAt
    }
  }
`

const listAnnouncementCommentsQuery = /* GraphQL */ `
  query ListAnnouncementComments(
    $postId: ID!
    $limit: Int
    $sortDirection: ModelSortDirection
  ) {
    listAnnouncementComments(postId: $postId, limit: $limit, sortDirection: $sortDirection) {
      commentId
      postId
      authorId
      authorDisplayName
      body
      createdAt
      likeCount
      hasLiked
    }
  }
`

const createAnnouncementPostMutation = /* GraphQL */ `
  mutation CreateAnnouncementPost($input: CreateAnnouncementPostInput!) {
    createAnnouncementPost(input: $input) {
      postId
      channelId
      title
      body
      authorId
      authorDisplayName
      urgency
      audience
      attachmentUrl
      attachmentType
      mentionIds
      createdAt
      editedAt
      commentCount
      readCount
      hasRead
      likeCount
      hasLiked
      pollVoteCounts
      pollVoterCount
      myPollVotes
    }
  }
`

const addAnnouncementCommentMutation = /* GraphQL */ `
  mutation AddAnnouncementComment($input: AddAnnouncementCommentInput!) {
    addAnnouncementComment(input: $input) {
      commentId
      postId
      authorId
      authorDisplayName
      body
      createdAt
      likeCount
      hasLiked
    }
  }
`

const markAnnouncementReadMutation = /* GraphQL */ `
  mutation MarkAnnouncementRead($postId: ID!, $channelId: AnnouncementChannel!) {
    markAnnouncementRead(postId: $postId, channelId: $channelId)
  }
`

const updateAnnouncementPostMutation = /* GraphQL */ `
  mutation UpdateAnnouncementPost(
    $postId: ID!
    $channelId: AnnouncementChannel!
    $input: UpdateAnnouncementPostInput!
  ) {
    updateAnnouncementPost(postId: $postId, channelId: $channelId, input: $input) {
      postId
      channelId
      title
      body
      authorId
      authorDisplayName
      urgency
      audience
      attachmentUrl
      attachmentType
      mentionIds
      createdAt
      editedAt
      commentCount
      readCount
      hasRead
      likeCount
      hasLiked
      pollVoteCounts
      pollVoterCount
      myPollVotes
    }
  }
`

const deleteAnnouncementPostMutation = /* GraphQL */ `
  mutation DeleteAnnouncementPost($postId: ID!, $channelId: AnnouncementChannel!) {
    deleteAnnouncementPost(postId: $postId, channelId: $channelId)
  }
`

const toggleAnnouncementLikeMutation = /* GraphQL */ `
  mutation ToggleAnnouncementLike($input: ToggleAnnouncementLikeInput!) {
    toggleAnnouncementLike(input: $input) {
      targetType
      targetId
      liked
      likeCount
    }
  }
`

const castAnnouncementVoteMutation = /* GraphQL */ `
  mutation CastAnnouncementVote($input: CastAnnouncementVoteInput!) {
    castAnnouncementVote(input: $input) {
      postId
      myPollVotes
      pollVoteCounts
      pollVoterCount
    }
  }
`

/** Shared AnnouncementPost selection for the member Forum ops (incl. status). */
const FORUM_POST_FIELDS = /* GraphQL */ `
  postId
  channelId
  title
  body
  authorId
  authorDisplayName
  authorRoleType
  urgency
  audience
  status
  attachmentUrl
  attachmentType
  mentionIds
  createdAt
  editedAt
  reviewedBy
  reviewedByName
  reviewedAt
  rejectionReason
  commentCount
  readCount
  hasRead
  likeCount
  hasLiked
  pollVoteCounts
  pollVoterCount
  myPollVotes
`

const submitForumPostMutation = /* GraphQL */ `
  mutation SubmitForumPost($input: SubmitForumPostInput!) {
    submitForumPost(input: $input) {
      ${FORUM_POST_FIELDS}
    }
  }
`

const reviewForumPostMutation = /* GraphQL */ `
  mutation ReviewForumPost(
    $postId: ID!
    $decision: ForumReviewDecision!
    $declineReasonTemplateId: String
    $declineReasonDetail: String
  ) {
    reviewForumPost(
      postId: $postId
      decision: $decision
      declineReasonTemplateId: $declineReasonTemplateId
      declineReasonDetail: $declineReasonDetail
    ) {
      ${FORUM_POST_FIELDS}
    }
  }
`

const listForumReviewPostsQuery = /* GraphQL */ `
  query ListForumReviewPosts($limit: Int, $nextToken: String) {
    listForumReviewPosts(limit: $limit, nextToken: $nextToken) {
      items {
        ${FORUM_POST_FIELDS}
      }
      nextToken
    }
  }
`

export async function listAnnouncementPosts(
  channelId: AnnouncementChannelEnum,
  options: { limit?: number; nextToken?: string | null } = {},
): Promise<AnnouncementPostConnection> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listAnnouncementPosts: AnnouncementPostConnection }>(
    listAnnouncementPostsQuery,
    {
      channelId,
      limit: options.limit ?? 25,
      nextToken: options.nextToken ?? undefined,
    },
  )
  return data.listAnnouncementPosts
}

export async function listNotifications(limit = 30): Promise<NotificationGql[]> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listNotifications: NotificationGql[] }>(
    listNotificationsQuery,
    { limit },
  )
  return data.listNotifications ?? []
}

export async function listAnnouncementComments(
  postId: string,
  options: { limit?: number; sortDirection?: 'ASC' | 'DESC' } = {},
): Promise<AnnouncementCommentGql[]> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listAnnouncementComments: AnnouncementCommentGql[] }>(
    listAnnouncementCommentsQuery,
    {
      postId,
      limit: options.limit ?? 50,
      sortDirection: options.sortDirection ?? 'ASC',
    },
  )
  return data.listAnnouncementComments ?? []
}

export async function createAnnouncementPost(input: {
  channelId: AnnouncementChannelEnum
  title: string
  body: string
  urgency?: AnnouncementUrgencyEnum
  audience?: AnnouncementAudienceEnum
  attachmentUrl?: string
  attachmentType?: AttachmentType
  mentionIds?: string[]
}): Promise<AnnouncementPostGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ createAnnouncementPost: AnnouncementPostGql }>(
    createAnnouncementPostMutation,
    { input },
  )
  return data.createAnnouncementPost
}

export async function addAnnouncementComment(input: {
  postId: string
  channelId: AnnouncementChannelEnum
  body: string
}): Promise<AnnouncementCommentGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ addAnnouncementComment: AnnouncementCommentGql }>(
    addAnnouncementCommentMutation,
    { input },
  )
  return data.addAnnouncementComment
}

export async function markAnnouncementRead(
  postId: string,
  channelId: AnnouncementChannelEnum,
): Promise<boolean> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ markAnnouncementRead: boolean }>(
    markAnnouncementReadMutation,
    { postId, channelId },
  )
  return data.markAnnouncementRead
}

export async function updateAnnouncementPost(input: {
  postId: string
  channelId: AnnouncementChannelEnum
  title: string
  body: string
  urgency: AnnouncementUrgencyEnum
  audience: AnnouncementAudienceEnum
  attachmentUrl?: string | null
  attachmentType?: AttachmentType | null
  mentionIds?: string[]
}): Promise<AnnouncementPostGql> {
  assertAppSyncConfigured()
  const { postId, channelId, ...fields } = input
  const data = await runGraphql<{ updateAnnouncementPost: AnnouncementPostGql }>(
    updateAnnouncementPostMutation,
    {
      postId,
      channelId,
      input: fields,
    },
  )
  return data.updateAnnouncementPost
}

export async function deleteAnnouncementPost(
  postId: string,
  channelId: AnnouncementChannelEnum,
): Promise<boolean> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ deleteAnnouncementPost: boolean }>(
    deleteAnnouncementPostMutation,
    { postId, channelId },
  )
  return data.deleteAnnouncementPost
}

export async function toggleAnnouncementLike(input: {
  targetType: AnnouncementLikeTarget
  targetId: string
  postId: string
  channelId: AnnouncementChannelEnum
}): Promise<AnnouncementLikeResultGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ toggleAnnouncementLike: AnnouncementLikeResultGql }>(
    toggleAnnouncementLikeMutation,
    { input },
  )
  return data.toggleAnnouncementLike
}

export async function castAnnouncementVote(input: {
  postId: string
  channelId: AnnouncementChannelEnum
  optionIds: string[]
}): Promise<AnnouncementVoteResultGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ castAnnouncementVote: AnnouncementVoteResultGql }>(
    castAnnouncementVoteMutation,
    { input },
  )
  return data.castAnnouncementVote
}

export async function submitForumPost(input: {
  title: string
  body: string
  attachmentUrl?: string | null
  attachmentType?: AttachmentType | null
  mentionIds?: string[]
}): Promise<AnnouncementPostGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ submitForumPost: AnnouncementPostGql }>(
    submitForumPostMutation,
    { input },
  )
  return data.submitForumPost
}

export async function reviewForumPost(
  postId: string,
  decision: ForumReviewDecision,
  options?: { declineReasonTemplateId?: string; declineReasonDetail?: string },
): Promise<AnnouncementPostGql> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ reviewForumPost: AnnouncementPostGql }>(
    reviewForumPostMutation,
    {
      postId,
      decision,
      declineReasonTemplateId: options?.declineReasonTemplateId,
      declineReasonDetail: options?.declineReasonDetail,
    },
  )
  return data.reviewForumPost
}

export async function listForumReviewPosts(
  options: { limit?: number; nextToken?: string | null } = {},
): Promise<AnnouncementPostConnection> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listForumReviewPosts: AnnouncementPostConnection }>(
    listForumReviewPostsQuery,
    {
      limit: options.limit ?? 25,
      nextToken: options.nextToken ?? undefined,
    },
  )
  return data.listForumReviewPosts
}

export async function getAnnouncementAttachmentUploadUrl(input: {
  announcementChannel: AnnouncementChannelEnum
  fileName: string
  contentType: string
  attachmentType: AttachmentType
}) {
  assertAppSyncConfigured()
  const data = await runGraphql<{
    getAttachmentUploadUrl: {
      uploadUrl: string
      attachmentUrl: string
      attachmentKey: string
    }
  }>(getAttachmentUploadUrlQuery, {
    input: {
      announcementChannel: input.announcementChannel,
      fileName: input.fileName,
      contentType: input.contentType,
      attachmentType: input.attachmentType,
    },
  })
  return data.getAttachmentUploadUrl
}

export function isAppSyncConfigured(): boolean {
  return Boolean(env.appsyncGraphqlUrl)
}

// --- Manage devices (app-owned registry) ---

export type UserDeviceStatus = 'pending' | 'trusted' | 'revoked'

export type UserDevice = {
  deviceId: string
  platform: string
  displayName: string
  appVersion: string | null
  createdAt: string
  lastSeenAt: string
  status: UserDeviceStatus
  biometricEnrolled: boolean
  lastIp: string | null
  approxLocation: string | null
  /** Issued once by register/heartbeat; saved by this module, never shown. */
  installSecret?: string | null
}

export type MyDevicesPayload = {
  devices: UserDevice[]
  trustedCount: number
  pendingCount: number
  maxTrusted: number
}

const userDeviceFields = `
  deviceId
  platform
  displayName
  appVersion
  createdAt
  lastSeenAt
  status
  biometricEnrolled
  lastIp
  approxLocation
`

const listMyDevicesQuery = /* GraphQL */ `
  query ListMyDevices {
    listMyDevices {
      devices { ${userDeviceFields} }
      trustedCount
      pendingCount
      maxTrusted
    }
  }
`

const registerMyDeviceMutation = /* GraphQL */ `
  mutation RegisterMyDevice($input: RegisterMyDeviceInput!) {
    registerMyDevice(input: $input) { ${userDeviceFields} installSecret }
  }
`

const heartbeatMyDeviceMutation = /* GraphQL */ `
  mutation HeartbeatMyDevice($input: HeartbeatMyDeviceInput!) {
    heartbeatMyDevice(input: $input) { ${userDeviceFields} installSecret }
  }
`

const renameMyDeviceMutation = /* GraphQL */ `
  mutation RenameMyDevice($input: RenameMyDeviceInput!) {
    renameMyDevice(input: $input) { ${userDeviceFields} }
  }
`

const approveMyDeviceMutation = /* GraphQL */ `
  mutation ApproveMyDevice($input: ApproveMyDeviceInput!) {
    approveMyDevice(input: $input) { ${userDeviceFields} }
  }
`

const revokeMyDeviceMutation = /* GraphQL */ `
  mutation RevokeMyDevice($input: RevokeMyDeviceInput!) {
    revokeMyDevice(input: $input) { ${userDeviceFields} }
  }
`

export async function listMyDevices(): Promise<MyDevicesPayload> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ listMyDevices: MyDevicesPayload }>(listMyDevicesQuery)
  return data.listMyDevices
}

const STILL_REGISTERING_RETRY_MS = 1_500

/**
 * Run one register/heartbeat with this install's secret, save any newly issued
 * secret BEFORE the next call can start, and retry once when a sibling call
 * (another tab / the checklist) is mid-claim.
 *
 * The signed-in user is resolved ONCE for the whole call: the secret is read
 * from and saved under that user's key only (and dropped if the account
 * changed while the request was in flight).
 *
 * `recoverFromLostSecret` (register): if the server answers "could not verify"
 * while we hold NO secret for this id (and the secure store itself was
 * readable), the id belongs to someone else's claim or a restored backup —
 * mint a NEW device id and register as a new pending install. The old row is
 * never re-claimed. A client that DOES hold a secret just surfaces the error.
 */
async function runWithInstallSecret<T extends UserDevice>(
  deviceId: string,
  call: (deviceId: string, secret: string | null) => Promise<T>,
  options: { recoverFromLostSecret?: boolean } = {},
): Promise<T> {
  return serialiseDeviceCall(async () => {
    const userId = await getInstallSecretUserId()
    let activeId = deviceId

    const attempt = async (allowRotate: boolean): Promise<T> => {
      const id = activeId
      const { secret, readable } = await readInstallSecretState(userId, id)
      let device: T
      try {
        device = await call(id, secret)
      } catch (err) {
        if (
          allowRotate &&
          options.recoverFromLostSecret &&
          !secret &&
          readable &&
          userId &&
          isDeviceVerificationError(err)
        ) {
          // Another tab may have claimed this id and saved the secret while we
          // were in flight. Re-read before minting a new id.
          const retry = await readInstallSecretState(userId, id)
          if (!retry.secret) {
            activeId = await rotateDeviceId()
            return attempt(false)
          }
          device = await call(id, retry.secret)
        } else {
          throw err
        }
      }
      if (device.installSecret && userId && (await getInstallSecretUserId()) === userId) {
        await saveInstallSecret(id, device.installSecret, userId)
      }
      // Never keep the secret on the object callers pass around.
      const { installSecret: _issued, ...rest } = device
      void _issued
      return rest as T
    }
    try {
      return await attempt(true)
    } catch (err) {
      if (!isStillRegisteringError(err)) throw err
      await new Promise((resolve) => setTimeout(resolve, STILL_REGISTERING_RETRY_MS))
      return attempt(false)
    }
  })
}

export async function registerMyDevice(input: {
  deviceId: string
  platform: string
  displayName: string
  appVersion?: string | null
  biometricEnrolled?: boolean | null
  approxLocation?: string | null
  pushTokenRef?: string | null
  /** AWSJSON `DevicePermissionReport` (sent only when it changed or daily). */
  permissions?: string
  /** AWSJSON `DeviceSetupReport`. */
  setup?: string
}): Promise<UserDevice> {
  assertAppSyncConfigured()
  return runWithInstallSecret(
    input.deviceId,
    async (deviceId, deviceSecret) => {
      const data = await runGraphql<{ registerMyDevice: UserDevice }>(registerMyDeviceMutation, {
        input: { ...input, deviceId, deviceSecret, claimSecret: true },
      })
      return data.registerMyDevice
    },
    { recoverFromLostSecret: true },
  )
}

export async function heartbeatMyDevice(input: {
  deviceId: string
  biometricEnrolled?: boolean | null
  approxLocation?: string | null
  appVersion?: string | null
  pushTokenRef?: string | null
  permissions?: string
  setup?: string
}): Promise<UserDevice> {
  assertAppSyncConfigured()
  // A heartbeat that is rejected for a lost secret does not rotate the id itself (it has no
  // display name / platform to register with); the caller falls back to registerMyDevice.
  return runWithInstallSecret(input.deviceId, async (deviceId, deviceSecret) => {
    const data = await runGraphql<{ heartbeatMyDevice: UserDevice }>(heartbeatMyDeviceMutation, {
      input: { ...input, deviceId, deviceSecret, claimSecret: true },
    })
    return data.heartbeatMyDevice
  })
}

export async function renameMyDevice(deviceId: string, displayName: string): Promise<UserDevice> {
  assertAppSyncConfigured()
  // The acting install proves itself with its own secret (self: its own; others: a trusted install's).
  const userId = await getInstallSecretUserId()
  const actorDeviceId = getDeviceId()
  const actorSecret = await readInstallSecret(actorDeviceId, userId)
  const data = await runGraphql<{ renameMyDevice: UserDevice }>(renameMyDeviceMutation, {
    input: { deviceId, displayName, actorDeviceId, actorSecret },
  })
  return data.renameMyDevice
}

export async function approveMyDevice(
  deviceId: string,
  approverDeviceId: string,
): Promise<UserDevice> {
  assertAppSyncConfigured()
  // The approving install proves itself with its own secret (a bare id is not enough).
  const approverSecret = await readInstallSecret(approverDeviceId, await getInstallSecretUserId())
  const data = await runGraphql<{ approveMyDevice: UserDevice }>(approveMyDeviceMutation, {
    input: { deviceId, approverDeviceId, approverSecret },
  })
  return data.approveMyDevice
}

export async function revokeMyDevice(deviceId: string): Promise<UserDevice> {
  assertAppSyncConfigured()
  // Acting install + its secret: only an active trusted install may remove OTHER devices.
  const actorDeviceId = getDeviceId()
  const actorSecret = await readInstallSecret(actorDeviceId, await getInstallSecretUserId())
  const data = await runGraphql<{ revokeMyDevice: UserDevice | null }>(revokeMyDeviceMutation, {
    input: { deviceId, actorDeviceId, actorSecret },
  })
  // A refused call can come back as `revokeMyDevice: null` without an error.
  if (!data.revokeMyDevice) throw new Error('Could not sign out that device.')
  return data.revokeMyDevice
}

/**
 * Email-code recovery for THIS install (only while none of the user's trusted
 * devices was used in the last 14 days). Rides on approveMyDevice with this
 * install as both target and approver, proving itself with its own secret.
 */
async function runEmailRecovery(
  extra: { requestEmailCode: true } | { emailCode: string },
): Promise<UserDevice> {
  assertAppSyncConfigured()
  const deviceId = getDeviceId()
  const approverSecret = await readInstallSecret(deviceId, await getInstallSecretUserId())
  const data = await runGraphql<{ approveMyDevice: UserDevice | null }>(approveMyDeviceMutation, {
    input: { deviceId, approverDeviceId: deviceId, approverSecret, ...extra },
  })
  if (!data.approveMyDevice) throw new Error('Could not verify this device. Try again.')
  return data.approveMyDevice
}

/** Email a one-time code to the account email. */
export function requestDeviceEmailCode(): Promise<UserDevice> {
  return runEmailRecovery({ requestEmailCode: true })
}

/** Check the emailed code; resolves with this install (trusted) on success. */
export function verifyDeviceEmailCode(code: string): Promise<UserDevice> {
  return runEmailRecovery({ emailCode: code })
}

export type WebMfaChallengeResult = {
  status: 'otp_sent' | 'verified' | string
  destinationHint: string | null
  expiresInSeconds: number | null
  resendAvailableInSeconds: number | null
}

const startWebMfaChallengeMutation = /* GraphQL */ `
  mutation StartWebMfaChallenge($input: StartWebMfaChallengeInput!) {
    startWebMfaChallenge(input: $input) {
      status
      destinationHint
      expiresInSeconds
      resendAvailableInSeconds
    }
  }
`

const verifyWebMfaChallengeMutation = /* GraphQL */ `
  mutation VerifyWebMfaChallenge($input: VerifyWebMfaChallengeInput!) {
    verifyWebMfaChallenge(input: $input) {
      status
      destinationHint
      expiresInSeconds
      resendAvailableInSeconds
    }
  }
`

const resendWebMfaChallengeMutation = /* GraphQL */ `
  mutation ResendWebMfaChallenge($input: ResendWebMfaChallengeInput!) {
    resendWebMfaChallenge(input: $input) {
      status
      destinationHint
      expiresInSeconds
      resendAvailableInSeconds
    }
  }
`

export async function startWebMfaChallenge(input: {
  deviceId: string
  password: string
}): Promise<WebMfaChallengeResult> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ startWebMfaChallenge: WebMfaChallengeResult }>(
    startWebMfaChallengeMutation,
    { input },
  )
  return data.startWebMfaChallenge
}

export async function verifyWebMfaChallenge(input: {
  deviceId: string
  code: string
}): Promise<WebMfaChallengeResult> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ verifyWebMfaChallenge: WebMfaChallengeResult }>(
    verifyWebMfaChallengeMutation,
    { input },
  )
  return data.verifyWebMfaChallenge
}

export async function resendWebMfaChallenge(input: {
  deviceId: string
  password: string
}): Promise<WebMfaChallengeResult> {
  assertAppSyncConfigured()
  const data = await runGraphql<{ resendWebMfaChallenge: WebMfaChallengeResult }>(
    resendWebMfaChallengeMutation,
    { input },
  )
  return data.resendWebMfaChallenge
}

